import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api/api";
import { QUERY_KEYS } from "@/lib/hooks/queryKeys";
import type { ApiResponse } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SecretOperationHookReturn } from "./types";

interface UseSecretOperationsProps {
  projectId: string;
}

export function useSecretOperations({
  projectId,
}: UseSecretOperationsProps): SecretOperationHookReturn {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const showToast = {
    secretAdded: (key: string) => {
      toast({
        title: "Secret added",
        description: `"${key}" has been saved successfully`,
        duration: 2000,
      });
    },
    secretUpdated: (key: string) => {
      toast({
        title: "Secret updated",
        description: `"${key}" has been updated successfully`,
        duration: 2000,
      });
    },
    secretDeleted: (key: string) => {
      toast({
        title: "Secret deleted",
        description: `"${key}" has been removed successfully`,
        duration: 2000,
      });
    },
    error: (title: string, description: string) => {
      toast({
        variant: "destructive",
        title,
        description,
        duration: 5000,
      });
    },
  };

  const { mutate: saveSecretMutation, isPending: isSaving } = useMutation({
    mutationFn: async ({
      key,
      value,
      isNewSecret,
    }: { key: string; value: string; isNewSecret: boolean }) => {
      const result = await api.setSecret(projectId, key, value);
      return { result, key, isNewSecret };
    },
    onMutate: async ({ key, isNewSecret }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.SECRETS, projectId],
      });

      // Snapshot the previous value
      const previousSecrets = queryClient.getQueryData<
        ApiResponse<{ secretKeys: string[] }>
      >([QUERY_KEYS.SECRETS, projectId]);

      // Optimistically update the cache
      queryClient.setQueryData<ApiResponse<{ secretKeys: string[] }>>(
        [QUERY_KEYS.SECRETS, projectId],
        (old) => {
          if (!old) {
            return old;
          }

          const currentKeys = old.data.secretKeys;
          const newKeys = currentKeys.includes(key)
            ? currentKeys
            : [...currentKeys, key];

          return {
            ...old,
            data: {
              ...old.data,
              secretKeys: newKeys,
            },
          };
        },
      );

      return { previousSecrets, key, isNewSecret };
    },
    onError: (error, _variables, context) => {
      // Rollback the optimistic update
      if (context?.previousSecrets) {
        queryClient.setQueryData(
          [QUERY_KEYS.SECRETS, projectId],
          context.previousSecrets,
        );
      }

      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      showToast.error("Failed to save secret", errorMessage);
    },
    onSuccess: ({ result, key, isNewSecret }, _variables) => {
      if (result) {
        // Success - show appropriate toast
        if (isNewSecret) {
          showToast.secretAdded(key);
        } else {
          showToast.secretUpdated(key);
        }

        // Invalidate queries to ensure data consistency
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SECRETS, projectId],
        });
      } else {
        // Failed but no error thrown - this might mean the message was queued
        showToast.error("Failed to save secret", "Please try again");
      }
    },
  });

  const { mutate: deleteSecretMutation, isPending: isDeleting } = useMutation({
    mutationFn: async (key: string) => {
      const result = await api.deleteSecret(projectId, key);
      return { result, key };
    },
    onMutate: async (key) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.SECRETS, projectId],
      });

      // Snapshot the previous value
      const previousSecrets = queryClient.getQueryData<
        ApiResponse<{ secretKeys: string[] }>
      >([QUERY_KEYS.SECRETS, projectId]);

      // Optimistically update the cache
      queryClient.setQueryData<ApiResponse<{ secretKeys: string[] }>>(
        [QUERY_KEYS.SECRETS, projectId],
        (old) => {
          if (!old) {
            return old;
          }

          return {
            ...old,
            data: {
              ...old.data,
              secretKeys: old.data.secretKeys.filter((k) => k !== key),
            },
          };
        },
      );

      return { previousSecrets, key };
    },
    onError: (error, _key, context) => {
      // Rollback the optimistic update
      if (context?.previousSecrets) {
        queryClient.setQueryData(
          [QUERY_KEYS.SECRETS, projectId],
          context.previousSecrets,
        );
      }

      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      showToast.error("Failed to delete secret", errorMessage);
    },
    onSuccess: ({ result, key }, _variables) => {
      if (result) {
        // Success
        showToast.secretDeleted(key);

        // Invalidate queries to ensure data consistency
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SECRETS, projectId],
        });
      } else {
        // Failed but no error thrown - this might mean the message was queued
        showToast.error("Failed to delete secret", "Please try again");
      }
    },
  });

  const saveSecret = async (key: string, value: string): Promise<void> => {
    // Determine if this is a new secret by checking current cache
    const currentSecrets = queryClient.getQueryData<
      ApiResponse<{ secretKeys: string[] }>
    >([QUERY_KEYS.SECRETS, projectId]);
    const isNewSecret = !currentSecrets?.data.secretKeys.includes(key);

    saveSecretMutation({ key, value, isNewSecret });
  };

  const deleteSecret = async (key: string): Promise<void> => {
    deleteSecretMutation(key);
  };

  return {
    saveSecret,
    deleteSecret,
    isLoading: isSaving || isDeleting,
  };
}
